import { useEffect, useState } from "react";

import { LoadingSpinner, Toast, ToggleButton } from "@newsela/angelou";
import { useRouter } from "next/router";

import AdminConsoleBasePage from "@/admin/AdminConsoleBasePage";
import globalToasterRef from "@/base/globalToasterRef";
import FormCards from "@/components/ImplementationGuide/components/FormCards";
import HeaderActionButtons from "@/components/ImplementationGuide/components/HeaderActionButtons/HeaderActionButtons";
import {
  SUBTITLE_FOR_ALL_FORMS_COMPLETED,
  SUBTITLE_FOR_INCOMPLETE_FORMS,
} from "@/components/ImplementationGuide/constants";
import useImplementationGuideCompletionRateGet from "@/components/ImplementationGuide/hooks/useImplementationGuideCompletionRateGet";
import ResultsPage from "@/components/ImplementationGuide/ResultsPage";
import {
  areAllFormsComplete,
  atLeastOneFormIsComplete,
} from "@/components/ImplementationGuide/utils";
import getDistrictUUID from "@/utils/getDistrictUUID";
import useUser from "@/users/useUser";
import ADMIN_CONSOLE_ROUTE_CONTENTS from "../constants/AdminConsoleRouteContents";
import {
  toggleContainerCss,
  toggleButtonCss,
  headerButtonsContainerCss,
} from "./AdminConsoleImplementationGuidePage.style";
import {
  ADMIN_CONSOLE_ADMIN_TOOLS_IMPLEMENTATION_GUIDE,
  BASE_URL,
} from "./AdminConsoleImplementationGuidePage.constants";

const AdminConsoleImplementationGuidePage = () => {
  const { query } = useRouter();
  const { data: user } = useUser();
  const districtName = user?.district_admin?.district.name as string;
  const districtUniqueId = getDistrictUUID(user);
  const [displayToggle, setDisplayToggle] = useState<boolean>();
  const [displayResultsPage, setDisplayResultsPage] = useState<boolean>();

  const pdfOutputUrlParam = Array.isArray(query["pdf_output"])
    ? query["pdf_output"][0]
    : query["pdf_output"];
  const isPDFOutput = pdfOutputUrlParam === "true";

  const {
    data: completionRates,
    loading: loadingCompletionRates,
    error: fetchForCompletionRatesFailed,
  } = useImplementationGuideCompletionRateGet(districtUniqueId ?? "");

  const areFormsCompleted =
    completionRates && areAllFormsComplete(completionRates);

  useEffect(() => {
    // Display toast messages to convey the error.
    if (!loadingCompletionRates) {
      if (fetchForCompletionRatesFailed) {
        if (
          fetchForCompletionRatesFailed
            .toString()
            .includes("There is no matching account id in")
        ) {
          globalToasterRef.current.displayToast({
            children:
              "Unable to find district's record in our customer management system, please reach out to your Newsela customer success manager for support.",
            statusColor: Toast.statusColor.danger,
            showIcon: true,
          });
        } else {
          globalToasterRef.current.displayToast({
            children:
              "We are unable to correctly identify your district information. Please reach out to your Newsela customer success manager for support.",
            statusColor: Toast.statusColor.warning,
            showIcon: true,
          });
        }
      }

      // Let's determine what UI the user should see based on the forms' completion rates.
      if (completionRates) {
        const oneFormIsComplete = atLeastOneFormIsComplete(completionRates);
        // Determines if the user should see the toggle to, well, toggle between the
        // form cards or the results page appearing below the fold.
        setDisplayToggle(oneFormIsComplete);

        // If all of the forms are complete, the user should see the IG results page by default.
        if (areFormsCompleted) {
          setDisplayResultsPage(true);
        }
      }
    }
  }, [loadingCompletionRates]);

  const handleToggleButtonSelection = () => {
    setDisplayResultsPage(!displayResultsPage);
  };

  const renderToggleButton = () => {
    return (
      <div css={toggleContainerCss}>
        <ToggleButton
          dataProps={{
            "data-qa-selector":
              "toggle-button-to-view-implementation-guide-results-page",
          }}
          label="Admin quick start preview"
          onClick={handleToggleButtonSelection}
          pressed={displayResultsPage}
          __cssFor={{
            Button: {
              root: toggleButtonCss,
            },
          }}
        />
      </div>
    );
  };

  if (loadingCompletionRates) {
    return <LoadingSpinner />;
  }

  if (isPDFOutput) {
    return (
      <ResultsPage
        districtUniqueId={districtUniqueId ?? ""}
        districtName={districtName ?? ""}
        isPDFOutput
      />
    );
  }

  return (
    <AdminConsoleBasePage
      title={`Admin quick start for ${districtName}`}
      pageDescription={
        areFormsCompleted
          ? SUBTITLE_FOR_ALL_FORMS_COMPLETED
          : SUBTITLE_FOR_INCOMPLETE_FORMS
      }
      breadcrumbsSection={{
        href: null,
        contents: ADMIN_CONSOLE_ROUTE_CONTENTS.ADMIN_QUICK_START,
      }}
      pageName={ADMIN_CONSOLE_ADMIN_TOOLS_IMPLEMENTATION_GUIDE}
    >
      <div css={headerButtonsContainerCss}>
        <div>
          {displayResultsPage && (
            <HeaderActionButtons
              areFormsCompleted={areFormsCompleted}
              downloadPDFPath="?pdf_output=true"
            />
          )}
        </div>
        {displayToggle && renderToggleButton()}
      </div>
      {displayResultsPage ? (
        <ResultsPage
          districtUniqueId={districtUniqueId ?? ""}
          districtName={districtName ?? ""}
        />
      ) : (
        completionRates && (
          <FormCards
            baseUrl={BASE_URL}
            fetchForCompletionRatesFailed={!!fetchForCompletionRatesFailed}
            completionRates={completionRates}
          />
        )
      )}
    </AdminConsoleBasePage>
  );
};

export default AdminConsoleImplementationGuidePage;
